import type { LayoutNode, LayoutRoot } from '@/state/tab.store';

/** sessionStorage: the layout must not outlive its session, and two windows keep separate ones. */
export const TAB_STORAGE_KEY = 'tab-state';

/**
 * Drops the stored snapshot, so the next boot starts from the default layout.
 *
 * Lives beside the key because both the store - on logout and on a 401 - and the persistence
 * service need it, and this module imports nothing from either, so neither has to reach through the
 * other to clear.
 */
export function clearStoredSnapshot(): void {
  try {
    sessionStorage.removeItem(TAB_STORAGE_KEY);
  } catch {
    // nothing to clear, or nothing we can do about it
  }
}

/** Bump when the shape below changes incompatibly; `parseSnapshot` discards anything else. */
export const TAB_SNAPSHOT_VERSION = 1;

/** A hand-edited snapshot must not be able to blow the stack during validation. */
const MAX_DEPTH = 32;

/**
 * Snapshot ids end up as object keys, where `__proto__` and friends corrupt the restored layout
 * instead of being rejected. Every real id is a uuid, so refusing them costs nothing.
 */
const UNSAFE_KEYS: ReadonlySet<string> = new Set(['__proto__', 'constructor', 'prototype']);

function isSafeId(value: unknown): value is string {
  return typeof value === 'string' && !UNSAFE_KEYS.has(value);
}

export interface TabStateSnapshot {
  version: number;
  root: { activeGroupId: string; layout: LayoutNodeSnapshot | null };
  sizes: Record<string, number[]>;
}

export interface GroupNodeSnapshot {
  id: string;
  type: 'group';
  activeTabId: string;
  tabs: TabItemSnapshot[];
}

export interface SplitNodeSnapshot {
  id: string;
  type: 'split';
  orientation: 'horizontal' | 'vertical';
  children: LayoutNodeSnapshot[];
}

export type LayoutNodeSnapshot = GroupNodeSnapshot | SplitNodeSnapshot;

export interface TabItemSnapshot {
  id: string;
  title: string;
  icon: string;
  /** From tab-registry, untrusted: narrow with `isTabKey` before looking a loader up. */
  componentKey: string;
  data?: unknown;
}

/** Ids are kept verbatim: the pane sizes and the active selection are keyed by them. */
export function toSnapshot(root: LayoutRoot, sizes: Record<string, number[]>): TabStateSnapshot {
  const layout = root.layout ? nodeToSnapshot(root.layout) : null;

  return {
    version: TAB_SNAPSHOT_VERSION,
    root: { activeGroupId: root.activeGroupId, layout },
    sizes: layout ? liveSizes(layout, sizes) : {},
  };
}

/**
 * The store keeps the sizes of collapsed splits so that going back restores their pane widths, so
 * its map outgrows the layout. Only what the layout still refers to is written.
 */
function liveSizes(node: LayoutNodeSnapshot, sizes: Record<string, number[]>): Record<string, number[]> {
  const live: Record<string, number[]> = {};

  const walk = (current: LayoutNodeSnapshot): void => {
    if (current.type !== 'split') {
      return;
    }

    const entry = Object.hasOwn(sizes, current.id) ? sizes[current.id] : undefined;
    if (entry) {
      live[current.id] = entry;
    }

    for (const child of current.children) {
      walk(child);
    }
  };

  walk(node);

  return live;
}

function nodeToSnapshot(node: LayoutNode): LayoutNodeSnapshot {
  if (node.type === 'group') {
    return {
      id: node.id,
      type: 'group',
      activeTabId: node.activeTabId,
      tabs: node.tabs.map((tab) => ({
        id: tab.id,
        title: tab.title,
        icon: tab.icon,
        componentKey: tab.componentKey,
        data: tab.data,
      })),
    };
  }

  return {
    id: node.id,
    type: 'split',
    orientation: node.orientation,
    children: node.children.map(nodeToSnapshot),
  };
}

/**
 * Never throws: a stale or hand-edited value costs the user their tab layout and nothing more, so
 * every failure returns `null` and the caller starts empty.
 */
export function parseSnapshot(raw: string | null): TabStateSnapshot | null {
  if (!raw) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isRecord(parsed) || parsed['version'] !== TAB_SNAPSHOT_VERSION) {
    return null;
  }

  const root = parsed['root'];
  if (!isRecord(root) || typeof root['activeGroupId'] !== 'string') {
    return null;
  }

  const layout = root['layout'];
  if (layout !== null && !isNodeSnapshot(layout, 0)) {
    return null;
  }

  const sizes = parsed['sizes'];
  if (!isSizes(sizes)) {
    return null;
  }

  return {
    version: TAB_SNAPSHOT_VERSION,
    root: { activeGroupId: root['activeGroupId'], layout: layout as LayoutNodeSnapshot | null },
    sizes,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSizes(value: unknown): value is Record<string, number[]> {
  return (
    isRecord(value) &&
    Object.keys(value).every(isSafeId) &&
    Object.values(value).every(
      (entry) => Array.isArray(entry) && entry.every((n) => typeof n === 'number' && Number.isFinite(n)),
    )
  );
}

function isNodeSnapshot(value: unknown, depth: number): boolean {
  if (depth > MAX_DEPTH || !isRecord(value) || !isSafeId(value['id'])) {
    return false;
  }

  if (value['type'] === 'group') {
    return (
      typeof value['activeTabId'] === 'string' && Array.isArray(value['tabs']) && value['tabs'].every(isTabItemSnapshot)
    );
  }

  if (value['type'] === 'split') {
    return (
      (value['orientation'] === 'horizontal' || value['orientation'] === 'vertical') &&
      Array.isArray(value['children']) &&
      value['children'].length > 0 &&
      value['children'].every((child) => isNodeSnapshot(child, depth + 1))
    );
  }

  return false;
}

function isTabItemSnapshot(value: unknown): boolean {
  return (
    isRecord(value) &&
    isSafeId(value['id']) &&
    typeof value['title'] === 'string' &&
    typeof value['icon'] === 'string' &&
    typeof value['componentKey'] === 'string'
  );
}
